import { getBGColor } from "@ameelio/ui";
import VolumeUp from "@mui/icons-material/VolumeUp";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import StepperFormWrapper from "../../../lib/StepperFormWrapper";
import { StepperType } from "../../../lib/StepperWrapper";
import { ScreenTitle } from "../../../lib/typography";
import { ltrTheme } from "../../../theme.ts";
import oceanSound from "../../LiveCall/sounds/oceanWaves.mp3";

type Props = {
  stepper: StepperType;
  onSubmit: (audioOk: boolean) => void | Promise<void>;
};

const DEFAULT_VOLUME = 0.5;

export default function TestAudioStep({ stepper, onSubmit }: Props) {
  const { t } = useTranslation();

  const [audioOk, setAudioOk] = useState(true);

  // Set up audio context and start playback
  useEffect(() => {
    let context: AudioContext | null = null;
    let audioEl: HTMLAudioElement | null = null;
    let gain: GainNode | null = null;

    const setupAudio = async () => {
      try {
        setAudioOk(true);
        context = new window.AudioContext();
        audioEl = new Audio(oceanSound);
        audioEl.loop = true;

        // Connect to gain node for volume control
        const source = context.createMediaElementSource(audioEl);
        gain = context.createGain();
        gain.gain.value = DEFAULT_VOLUME;
        source.connect(gain);
        gain.connect(context.destination);

        await audioEl.play();
      } catch (err) {
        setAudioOk(false);
        console.error("Error setting up audio:", err);
      }
    };

    setupAudio();

    // Clean up on unmount
    return () => {
      if (audioEl) {
        audioEl.pause();
        audioEl.src = "";
      }
      if (context) {
        context.close();
      }
    };
  }, []);

  const color = ltrTheme.palette.primary.light;
  const lightColor = getBGColor("light");

  return (
    <StepperFormWrapper
      stepper={stepper({})}
      handleSubmit={() => {
        onSubmit(audioOk);
      }}
    >
      <ScreenTitle>{t("Check speakers")}</ScreenTitle>
      <Stack
        sx={{
          height: "calc(100vh - 400px)",
          mt: 4,
          mb: 6,
        }}
      >
        <Typography sx={{ mb: 2 }}>
          {t("Do you hear a sound playing right now?")}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            height: 60,
            width: 60,
            borderRadius: "50%",
            display: "flex",
            mx: "auto",
            alignItems: "center",
            justifyContent: "center",
            animation: "pulse 1.5s infinite",
            "@keyframes pulse": {
              "0%": {
                boxShadow: `0 0 0 0 ${lightColor}`,
              },
              "70%": {
                boxShadow: `0 0 20px 10px ${color}`,
              },
              "100%": {
                boxShadow: `0 0 0 0 ${color}`,
              },
            },
          }}
        >
          <VolumeUp
            sx={{ fontSize: 50 }}
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion -- t()'s return type widens without .toString() because aria-label's contextual type changes which overload TS picks
            aria-label={t("Check speakers").toString()}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />
      </Stack>
    </StepperFormWrapper>
  );
}
